import React, { useState, useEffect, useRef } from "react";
import {
  Popover,
  Button,
  Slider,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const NumberRangePopup = ({
  handlePopupClose,
  column,
  min,
  max,
  setMinMaxRange,
  selectedRange,
  label = ''
}) => {
  const [range, setRange] = useState([selectedRange[0], selectedRange[1]]);
  const [minValue, setMinValue] = useState(selectedRange[0].toString());
  const [maxValue, setMaxValue] = useState(selectedRange[1].toString());
  const [invalidMinValue, setInvalidMinValue] = useState(false);
  const [invalidMaxValue, setInvalidMaxValue] = useState(false);

  useEffect(() => {
    setMinValue(selectedRange[0].toString());
    setMaxValue(selectedRange[1].toString());
    setRange([selectedRange[0], selectedRange[1]]);
  }, [selectedRange]);

  const handleClose = () => {
    handlePopupClose(false);
  };

  const validateMin = (val) => {
    if(!val || val === ''){
      return false;
    }
    const num = Number(val);
    //if selected number is greater than minimum value and 
    // less than maximum value and selected max value, it is valid, otherwise return false
    return !isNaN(num) && num >= min && num <= max && num <= Number(maxValue);
  };

  const validateMax = (val) => {
    if(!val || val === ''){
      return false;
    }
    const num = Number(val);
    return !isNaN(num) && num <= max && num >= min && num >= Number(minValue);
  };

  const handleMinChange = (e) => {
    const value = e.target.value;
    if(!value || value === ''){
      setInvalidMinValue(true);
    } else {
      setInvalidMinValue(false);
    }
    setMinValue(value);
    if(validateMin(value)) {
      setRange([value, range[1]]);
    }
  };

  const handleMaxChange = (e) => {
    const value = e.target.value;
    if(!value || value === ''){
      setInvalidMaxValue(true);
    } else {
      setInvalidMaxValue(false);
    }
    setMaxValue(value);
    if(validateMax(value)) {
      setRange([range[0], value]);
    }
  };

  const handleSliderChange = (event, newValue) => {
    setRange(newValue);
    setMinValue(newValue[0].toString());
    setMaxValue(newValue[1].toString());
  };

  const handleClearRange = () => {
    setMinValue(min.toString());
    setMaxValue(max.toString());
    setRange([min, max]);
  };

  const handleApplyRange = () => {
    const minNum = Number(minValue);
    const maxNum = Number(maxValue);
    if (!isNaN(minNum) && !isNaN(maxNum)) {
      let clampedMin = Math.max(min, Math.min(minNum, maxNum));
      let clampedMax = Math.max(clampedMin, Math.min(maxNum, max));
      if(invalidMinValue) {
        clampedMin = min;
      }
      if(invalidMaxValue) {
        clampedMax = max;
      }
      setMinMaxRange([clampedMin, clampedMax]);
    }
  };

  const id = "number-range-popover";

  const marks = min!=max?[
    { value: min, label: "Min" },
    { value: max, label: "Max" },
  ]:[
    { value: -10, label: "Min" },
    { value: 10, label: "Max" },
  ];

  return (
    <div>
      <Popover
        id={id}
        open={true}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
      >
        <div style={{ padding: "50px", width: "500px", position: "relative" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography gutterBottom>{label || column.toUpperCase() }</Typography>

          <Slider
            value={range}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            min={min}
            max={max}
            marks={marks}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "10px",
            }}
          >
            <TextField
              label="Min"
              type="text"
              value={minValue}
              onChange={handleMinChange}
              inputProps={{ inputMode: "numeric" }}
              style={{ flex: 1, marginRight: "10px" }}
            />
            <TextField
              label="Max"
              type="text"
              value={maxValue}
              onChange={handleMaxChange}
              inputProps={{ inputMode: "numeric" }}
              style={{ flex: 1 }}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
              gap: "10px",
            }}
          >
            <Button variant="outlined" onClick={handleClearRange} size="small">
              Clear Range
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyRange}
              disabled = {!validateMin(minValue) || !validateMax(maxValue)}
              size="small"
            >
              Apply Range
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default NumberRangePopup;
